// File: src/lib/utils.ts
// Purpose: Utility functions for InstaDev
// InstaDev - Core Utilities

import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

import * as pdfjsLib from "pdfjs-dist";
import mammoth from "mammoth";

// Ensure PDF.js worker is properly set
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: Date | string | undefined, format: 'short' | 'long' | 'relative' = 'short'): string {
  if (!date) return ''
  const d = typeof date === 'string' ? new Date(date) : date

  if (format === 'relative') {
    const now = new Date()
    const diff = now.getTime() - d.getTime()
    const seconds = Math.floor(diff / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (days > 0) return `${days}d ago`
    if (hours > 0) return `${hours}h ago`
    if (minutes > 0) return `${minutes}m ago`
    return 'Just now'
  }

  if (format === 'long') {
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

export function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M'
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K'
  }
  return num.toString()
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

export function generateId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36)
}

export function debounce<T extends (...args: unknown[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

export function throttle<T extends (...args: unknown[]) => void>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => {
        inThrottle = false
      }, limit)
    }
  }
}

export function formatDateForInput(value: any) {
  if (!value) return ''
  // If it's already an input-friendly date string, use it as-is
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value
  }
  // Try to build from number-like timestamp or Date-compatible value
  const date = new Date(typeof value === 'number' ? value : Number(value))
  if (isNaN(date.getTime())) {
    const fallback = new Date(value)
    if (isNaN(fallback.getTime())) return ''
    const y = fallback.getFullYear()
    const m = String(fallback.getMonth() + 1).padStart(2, '0')
    const d = String(fallback.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function formatTimeForInput(value: any) {
  console.log("Time", value);
  if (!value) return '';

  if (typeof value === 'string' && /^\d{2}:\d{2}(:\d{2})?$/.test(value)) {
    return value.length === 5 ? value : value.substring(0, 5); // Only HH:MM
  }

  const date = new Date(typeof value === 'number' ? value : Number(value));

  if (isNaN(date.getTime())) {
    const fallback = new Date(value);
    if (isNaN(fallback.getTime())) return '';
    const h = String(fallback.getHours()).padStart(2, '0');
    const m = String(fallback.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  }

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${hours}:${minutes}`;
}

export function utcToLocalTime(utcTimeString: string) {
  // Split "HH:mm"
  const [hours, minutes] = utcTimeString.split(":").map(Number);

  // Create a Date in UTC for today
  const now = new Date();
  const utcDate = new Date(Date.UTC(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    hours,
    minutes,
    0
  ));

  // Format local time in 12-hour format
  return utcDate.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });
}

export async function getDeviceInfo() {
  // 1. Device ID (persisted in localStorage)
  let deviceId = localStorage.getItem("deviceId");
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem("deviceId", deviceId);
  }

  // 2. Device Type
  const ua = navigator.userAgent;
  let deviceType = "Desktop";
  if (/mobile/i.test(ua)) deviceType = "Mobile";
  else if (/tablet/i.test(ua)) deviceType = "Tablet";

  // 3. IP Address (using ipify)
  let ipAddress = null;
  try {
    const res = await fetch("https://api.ipify.org?format=json");
    const data = await res.json();
    ipAddress = data.ip;
  } catch (err) {
    console.warn("Failed to fetch IP:", err);
  }

  // 4. Location
  let latitude = null, longitude = null;
  try {
    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      if (!navigator.geolocation) {
        return reject("Geolocation not supported");
      }
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      });
    });
    latitude = position.coords.latitude;
    longitude = position.coords.longitude;
  } catch (err) {
    console.warn("Failed to get location:", err);
  }

  return { deviceId, deviceType, ipAddress, latitude, longitude };
}

export function isoToLocalDateString(dateInput: string | number | Date): string {
  if (!dateInput) {
    return ''
  }

  let date: Date

  // Handle different input types
  if (dateInput instanceof Date) {
    date = dateInput
  } else if (typeof dateInput === 'number') {
    // Handle epoch timestamp (both seconds and milliseconds)
    // If the number is less than 10^10, assume it's in seconds, otherwise milliseconds
    const timestamp = dateInput < 10000000000 ? dateInput * 1000 : dateInput
    date = new Date(timestamp)
  } else if (typeof dateInput === 'string') {
    // Handle ISO string, regular date string, or string representation of epoch
    if (/^\d+$/.test(dateInput)) {
      // String contains only digits, treat as epoch timestamp
      const timestamp = parseInt(dateInput, 10)
      const finalTimestamp = timestamp < 10000000000 ? timestamp * 1000 : timestamp
      date = new Date(finalTimestamp)
    } else {
      // Treat as ISO string or other date string format
      date = new Date(dateInput)
    }
  } else {
    return ''
  }

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    console.warn('Invalid date input provided:', dateInput)
    return ''
  }

  // Convert to local date string (removes time portion)
  return date.toLocaleDateString()
}


export async function extractTextFromFile(file: File): Promise<string> {
  const extension = file.name.split(".").pop()?.toLowerCase();

  if (extension === "pdf") {
    return extractPdfText(file);
  } else if (extension === "docx") {
    return extractDocxText(file);
  } else {
    throw new Error("Unsupported file type. Only PDF and DOCX are supported.");
  }
}

async function extractPdfText(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  let text = "";

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    text += content.items.map((item: any) => item.str).join(" ") + "\n";
  }

  return text.trim();
}

async function extractDocxText(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value.trim();
}
